import type { VisualEditorFieldSchema } from '../../types.ts';
import { escapeHtml } from './VisualEditorDrawerConstants.ts';

export function renderFieldInput(
  field: VisualEditorFieldSchema,
  editedValues: Record<string, unknown>
): string {
  const val = editedValues[field.key] !== undefined ? editedValues[field.key] : field.value;
  const strVal = val !== null && val !== undefined ? String(val) : '';

  switch (field.type) {
    case 'textarea':
      return `
        <div class="ve-form-group">
          <div class="ve-label-row">
            <label class="ve-label" for="ve-input-${field.key}">${field.label}</label>
            <span class="ve-cad-key">${field.key}</span>
          </div>
          <textarea id="ve-input-${field.key}" class="ve-textarea" data-key="${field.key}">${strVal}</textarea>
        </div>
      `;

    case 'number':
      return `
        <div class="ve-form-group">
          <div class="ve-label-row">
            <label class="ve-label" for="ve-input-${field.key}">${field.label}</label>
            <span class="ve-cad-key">${field.key}</span>
          </div>
          <div class="ve-number-group">
            <button type="button" class="ve-stepper-btn btn-step-down" data-target="ve-input-${field.key}">-</button>
            <input type="number" id="ve-input-${field.key}" class="ve-input" value="${strVal}" data-key="${field.key}" />
            <button type="button" class="ve-stepper-btn btn-step-up" data-target="ve-input-${field.key}">+</button>
          </div>
        </div>
      `;

    case 'boolean':
      const checked = val === true || val === 'true';
      return `
        <div class="ve-form-group">
          <div class="ve-switch-container">
            <div>
              <label class="ve-label" for="ve-switch-${field.key}">${field.label}</label>
              <div class="ve-cad-key">${field.key}</div>
            </div>
            <label class="ve-switch">
              <input type="checkbox" id="ve-switch-${field.key}" data-key="${field.key}" ${checked ? 'checked' : ''} />
              <span class="ve-switch-slider"></span>
            </label>
          </div>
        </div>
      `;

    case 'select':
      const options = field.options || ['Standard', 'FullBleed', 'Centered', 'Split'];
      const optionsHtml = options
        .map((opt) => `<option value="${opt}" ${opt === strVal ? 'selected' : ''}>${opt}</option>`)
        .join('');
      return `
        <div class="ve-form-group">
          <div class="ve-label-row">
            <label class="ve-label" for="ve-input-${field.key}">${field.label}</label>
            <span class="ve-cad-key">${field.key}</span>
          </div>
          <select id="ve-input-${field.key}" class="ve-select" data-key="${field.key}">
            ${optionsHtml}
          </select>
        </div>
      `;

    case 'image':
    case 'file':
      const fileUrls: string[] = Array.isArray(val)
        ? val.map(String).filter((s) => s.trim().length > 0)
        : strVal
          ? strVal
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
          : [];

      let previewsHtml = '';
      if (fileUrls.length > 0) {
        previewsHtml = `
          <div class="ve-file-previews-list" id="ve-previews-${field.key}">
            ${fileUrls
              .map((url, idx) => {
                const isImg =
                  url.match(/\.(jpg|jpeg|png|webp|svg|gif)$/i) || url.startsWith('data:image');
                const fileName = url.split('/').pop() || 'Uploaded File';
                return `
              <div class="ve-file-preview" id="ve-preview-${field.key}-${idx}" data-file-url="${escapeHtml(url)}">
                ${isImg ? `<img src="${url}" class="ve-preview-thumb" alt="Preview" />` : `<span class="ve-preview-doc-badge" style="display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border:1px solid #334155;border-radius:4px;font-size:10px;font-family:monospace;font-weight:700;">[ DOC ]</span>`}
                <div class="ve-preview-info">
                  <span class="ve-preview-name" title="${escapeHtml(fileName)}">${escapeHtml(fileName)}</span>
                  <span class="ve-preview-url" title="${escapeHtml(url)}">${escapeHtml(url)}</span>
                </div>
                <div class="ve-preview-actions">
                  <button type="button" class="ve-btn-remove-file loumars-remove-file-btn" data-key="${field.key}" data-index="${idx}" data-url="${escapeHtml(url)}" data-filename="${escapeHtml(fileName)}" title="Delete or remove attached file" aria-label="Remove attached file ${escapeHtml(fileName)}">
                    Remove
                  </button>
                </div>
              </div>`;
              })
              .join('')}
          </div>
        `;
      }

      return `
        <div class="ve-form-group">
          <div class="ve-label-row">
            <label class="ve-label" for="ve-input-${field.key}">${field.label}</label>
            <span class="ve-cad-key">${field.key}</span>
          </div>
          <input type="text" id="ve-input-${field.key}" class="ve-input" value="${strVal}" data-key="${field.key}" placeholder="https://... or upload file" />
          
          <div class="ve-dropzone" data-key="${field.key}">
            <input type="file" id="ve-file-${field.key}" class="ve-file-input" style="display:none;" />
            <div class="ve-dropzone-content">
              <span class="ve-dropzone-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg></span>
              <span class="ve-dropzone-text">Click or drag & drop to upload file / media</span>
            </div>
          </div>

          ${previewsHtml}
        </div>
      `;

    case 'url':
      return `
        <div class="ve-form-group">
          <div class="ve-label-row">
            <label class="ve-label" for="ve-input-${field.key}">${field.label}</label>
            <span class="ve-cad-key">${field.key}</span>
          </div>
          <input type="url" id="ve-input-${field.key}" class="ve-input" value="${strVal}" data-key="${field.key}" placeholder="https:// or /route" />
        </div>
      `;

    default: // text
      return `
        <div class="ve-form-group">
          <div class="ve-label-row">
            <label class="ve-label" for="ve-input-${field.key}">${field.label}</label>
            <span class="ve-cad-key">${field.key}</span>
          </div>
          <input type="text" id="ve-input-${field.key}" class="ve-input" value="${strVal}" data-key="${field.key}" />
        </div>
      `;
  }
}
